import { resolutionStatus } from '../components/resolutionStatus'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Camera, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { TrendChart } from '../components/TrendChart'
import { useKnowledgeHealth, useSnapshotHealth } from '../hooks/useKnowledge'
import type { HealthMetrics } from '../api/knowledgeApi'

const pct = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`)
const num = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString())

const ACTION_LABEL: Record<string, string> = {
  merge: 'Merges', split: 'Splits', link: 'Relations', category: 'Categories', generic: 'Generic', reject: 'Not concepts',
}

function Delta({ now, then, asPct }: { now: number; then: number | undefined; asPct?: boolean }) {
  if (then == null) return null
  const d = now - then
  if (Math.abs(d) < (asPct ? 0.0005 : 0.5)) return <span className="text-xs text-slate-400">no change</span>
  const text = asPct ? `${d > 0 ? '+' : ''}${(d * 100).toFixed(1)} pts` : `${d > 0 ? '+' : ''}${Math.round(d).toLocaleString()}`
  return <span className={`text-xs ${d > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{text}</span>
}

function Headline({ label, value, hint, delta }: { label: string; value: string; hint: string; delta?: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold tabular-nums">{value}</span>
          {delta}
        </div>
        <div className="text-[11px] leading-snug text-slate-500">{hint}</div>
      </CardContent>
    </Card>
  )
}

function pending(m: HealthMetrics) {
  return Object.values(m.proposals || {}).reduce((t, p) => t + (p.pending || 0), 0)
}

export function ProgressPage() {
  const { data, isLoading } = useKnowledgeHealth(90)
  const snap = useSnapshotHealth()

  if (isLoading || !data) {
    return (
      <KnowledgeFrame title="Progress" description="How far the knowledge graph has been formalized.">
        <Skeleton className="h-96 w-full" />
      </KnowledgeFrame>
    )
  }
  const { now, history, runs, acceptance } = data
  const first = history[0]?.metrics
  const times = history.map((h) => h.takenAt)
  const running = runs.find((r) => resolutionStatus(r) === 'running')

  return (
    <KnowledgeFrame
      title="Progress"
      description={
        <>
          How the graph is being hardened into formal knowledge: the share of artwork links whose
          concept has a category or has been reviewed by a person, the duplicates the builds have merged,
          the junk that no longer counts, the typed relations added — and how much of what the model
          proposes you accept. Measured after every sync and proposal pull; changes are against the
          first snapshot in the last 90 days.
        </>
      }
    >
      {running && (
        <Card className="border-violet-200 bg-violet-50">
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-1 p-4 text-sm text-violet-900">
            <span className="inline-flex items-center gap-2 font-medium">
              <Loader2 className="h-4 w-4 animate-spin" /> Resolution run in progress · {running.model}
            </span>
            <span>{num(running.verdicts)} verdicts · {num(running.proposals)} proposals saved</span>
            <span>${(running.spentUsd ?? 0).toFixed(2)} of ${(running.budgetUsd ?? 0).toFixed(2)}</span>
            <span>calibration {running.calibrationCatch != null ? pct(running.calibrationCatch) : 'not yet measured'}</span>
            <span className="text-violet-700">
              updated {running.finishedAt ? formatDistanceToNow(new Date(running.finishedAt), { addSuffix: true }) : '—'}; proposals are pulled hourly
            </span>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Headline
          label="Links on categorised concepts"
          value={pct(now.categorisedLinkShare)}
          hint={`${num(now.categorisedConcepts)} of ${num(now.concepts)} concepts have a category`}
          delta={<Delta now={now.categorisedLinkShare} then={first?.categorisedLinkShare} asPct />}
        />
        <Headline
          label="Links on reviewed concepts"
          value={pct(now.reviewedLinkShare)}
          hint={`${num(now.reviewedConcepts)} concepts with a person's decision`}
          delta={<Delta now={now.reviewedLinkShare} then={first?.reviewedLinkShare} asPct />}
        />
        <Headline
          label="Generic links down-weighted"
          value={pct(now.downweightedLinkShare)}
          hint={`${num(now.removalPending.concepts)} removals pending (${num(now.removalPending.links)} links)`}
          delta={<Delta now={now.downweightedLinkShare} then={first?.downweightedLinkShare} asPct />}
        />
        <Headline
          label="Duplicate concept IDs merged"
          value={num(now.mergedIds)}
          hint={`${num(now.rules.merge?.applied ?? 0)} curated merges applied by the build`}
          delta={<Delta now={now.mergedIds} then={first?.mergedIds} />}
        />
        <Headline
          label="Typed relations"
          value={num(now.relationsTotal)}
          hint="broader / narrower, variants, opposites"
          delta={<Delta now={now.relationsTotal} then={first?.relationsTotal} />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Formalized share of artwork links</CardTitle>
            <CardDescription>Categorised, reviewed by a person, and down-weighted as generic.</CardDescription>
          </CardHeader>
          <CardContent>
            <TrendChart
              times={times}
              max={1}
              formatValue={(v) => `${Math.round(v * 100)}%`}
              series={[
                { label: 'categorised', color: '#7c3aed', values: history.map((h) => h.metrics.categorisedLinkShare) },
                { label: 'reviewed', color: '#16a34a', values: history.map((h) => h.metrics.reviewedLinkShare) },
                { label: 'down-weighted', color: '#ea580c', values: history.map((h) => h.metrics.downweightedLinkShare) },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Structure and the queue</CardTitle>
            <CardDescription>Relations added, model proposals waiting, and duplicate IDs merged by builds.</CardDescription>
          </CardHeader>
          <CardContent>
            <TrendChart
              times={times}
              series={[
                { label: 'relations', color: '#0891b2', values: history.map((h) => h.metrics.relationsTotal) },
                { label: 'proposals waiting', color: '#db2777', values: history.map((h) => pending(h.metrics)) },
                { label: 'merged IDs', color: '#64748b', values: history.map((h) => h.metrics.mergedIds) },
              ]}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">The model&apos;s precision, by what you decided</CardTitle>
            <CardDescription>
              Of the proposals you have accepted or rejected, the share accepted. This is the number that
              says whether the resolution model can be trusted with more.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proposal</TableHead>
                  <TableHead className="text-right">Decided</TableHead>
                  <TableHead className="text-right">Accepted</TableHead>
                  <TableHead className="text-right">Waiting</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(acceptance).map(([action, a]) => (
                  <TableRow key={action}>
                    <TableCell>
                      <Link to={`/app/knowledge/proposals?action=${action}`} className="hover:underline">{ACTION_LABEL[action] ?? action}</Link>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{num(a.decided)}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.rate == null ? '—' : pct(a.rate)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(a.pending)}</TableCell>
                  </TableRow>
                ))}
                {Object.keys(acceptance).length === 0 && (
                  <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-slate-500">No proposals yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Resolution runs</CardTitle>
            <CardDescription>
              {num(now.ser.runs)} runs · ${now.ser.spent.toFixed(2)} spent · {num(now.ser.verdicts)} verdicts → {num(now.ser.proposals)} proposals
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Run</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Verdicts</TableHead>
                  <TableHead className="text-right">Spent</TableHead>
                  <TableHead className="text-right">Calibration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((r) => (
                  <TableRow key={r.runId}>
                    <TableCell>
                      <div className="text-sm">{r.startedAt ? formatDistanceToNow(new Date(r.startedAt), { addSuffix: true }) : r.runId}</div>
                      <div className="font-mono text-[10px] text-slate-400">{r.model}</div>
                    </TableCell>
                    <TableCell className="text-xs">{!r.candidates && !r.verdicts && r.status !== 'running' ? 'nothing new' : r.status}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(r.verdicts)}</TableCell>
                    <TableCell className="text-right tabular-nums">${(r.spentUsd ?? 0).toFixed(2)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${r.calibrationPassed ? 'text-emerald-700' : r.verdicts ? 'text-rose-700' : 'text-slate-400'}`}>
                      {r.calibrationCatch == null ? '—' : pct(r.calibrationCatch)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm text-slate-600">
          <span>
            Still to do: {num(pending(now))} proposals waiting · review queue {num(now.review.generic)} generic,{' '}
            {num(now.review.suspectMerge)} suspect merges, {num(now.review.duplicate)} duplicates,{' '}
            {num(now.review.uncategorised)} uncategorised
          </span>
          <Button size="sm" variant="outline" disabled={snap.isPending} onClick={() => snap.mutate()}>
            <Camera className="mr-1.5 h-3.5 w-3.5" /> Snapshot now
          </Button>
        </CardContent>
      </Card>
    </KnowledgeFrame>
  )
}
